import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { MapPin, Plus, Pencil, Trash2, QrCode, Download, Navigation } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_baduwork-tracker/artifacts/8tdmywqb_Badu%20Facility%20Services.png";

export default function LocationsPage() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    latitude: "",
    longitude: "",
    radius: 100
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      toast.error("Erro ao carregar locais");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        radius: parseInt(formData.radius)
      };

      if (selectedLocation) {
        await axios.put(`${API}/locations/${selectedLocation.id}`, data);
        toast.success("Local atualizado com sucesso!");
      } else {
        await axios.post(`${API}/locations`, data);
        toast.success("Local criado com sucesso!");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar local");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/locations/${selectedLocation.id}`);
      toast.success("Local removido com sucesso!");
      setDeleteDialogOpen(false);
      setSelectedLocation(null);
      fetchLocations();
    } catch (error) {
      toast.error("Erro ao remover local");
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData({
            ...formData,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6)
          });
          toast.success("Localização obtida!");
        },
        () => {
          toast.error("Não foi possível obter a localização");
        }
      );
    }
  };

  const openEditDialog = (location) => {
    setSelectedLocation(location);
    setFormData({
      name: location.name,
      address: location.address,
      latitude: location.latitude.toString(),
      longitude: location.longitude.toString(),
      radius: location.radius
    });
    setDialogOpen(true);
  };

  const openQrDialog = (location) => {
    setSelectedLocation(location);
    setQrDialogOpen(true);
  };

  const openDeleteDialog = (location) => {
    setSelectedLocation(location);
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedLocation(null);
    setFormData({ name: "", address: "", latitude: "", longitude: "", radius: 100 });
  };

  const downloadQr = () => {
    if (!selectedLocation?.qr_code) return;
    
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${selectedLocation.qr_code}`;
    link.download = `QR_${selectedLocation.name.replace(/\s+/g, "_")}.png`;
    link.click();
    toast.success("QR Code baixado!");
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in" data-testid="locations-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 font-manrope">Locais de Trabalho</h1>
            <p className="text-gray-500 mt-1">{locations.length} de 20 locais cadastrados</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                className="btn-primary text-white font-semibold"
                disabled={locations.length >= 20}
                data-testid="add-location-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Adicionar
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200">
              <DialogHeader>
                <DialogTitle className="text-gray-800">
                  {selectedLocation ? "Editar Local" : "Novo Local"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Nome do Local</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Escritório Central"
                    required
                    className="bg-gray-50 border-gray-200 text-gray-800"
                    data-testid="location-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Endereço</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Rua, número, cidade"
                    required
                    className="bg-gray-50 border-gray-200 text-gray-800"
                    data-testid="location-address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Latitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                      placeholder="-23.5505"
                      required
                      className="bg-gray-50 border-gray-200 text-gray-800"
                      data-testid="location-lat-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Longitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={formData.longitude}
                      onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                      placeholder="-46.6333"
                      required
                      className="bg-gray-50 border-gray-200 text-gray-800"
                      data-testid="location-lng-input"
                    />
                  </div>
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full border-gray-200 text-gray-600 hover:bg-gray-50"
                  onClick={getCurrentLocation}
                  data-testid="get-location-btn"
                >
                  <Navigation className="mr-2 h-4 w-4" />
                  Usar Localização Atual
                </Button>
                <div className="space-y-2">
                  <Label className="text-gray-700">Raio de Validação (metros)</Label>
                  <Input
                    type="number"
                    value={formData.radius}
                    onChange={(e) => setFormData({ ...formData, radius: e.target.value })}
                    placeholder="100"
                    className="bg-gray-50 border-gray-200 text-gray-800"
                    data-testid="location-radius-input"
                  />
                  <p className="text-xs text-gray-400">Distância máxima permitida para registrar ponto</p>
                </div>
                <div className="flex gap-3 pt-4">
                  <DialogClose asChild>
                    <Button type="button" variant="outline" className="flex-1 border-gray-200 text-gray-600 hover:bg-gray-50">
                      Cancelar
                    </Button>
                  </DialogClose>
                  <Button type="submit" className="flex-1 btn-primary text-white font-semibold" data-testid="save-location-btn">
                    {selectedLocation ? "Salvar" : "Criar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Locations Grid */}
        {locations.length === 0 ? (
          <Card className="bg-white border-gray-100 shadow-sm">
            <CardContent className="py-16">
              <div className="empty-state">
                <MapPin className="empty-state-icon" />
                <p className="empty-state-title">Nenhum local cadastrado</p>
                <p className="empty-state-description">
                  Clique em "Adicionar" para cadastrar o primeiro local de trabalho
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {locations.map((location) => (
              <Card key={location.id} className="bg-white border-gray-100 shadow-sm card-hover" data-testid={`location-card-${location.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-[#2ECC40]" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{location.name}</p>
                        <p className="text-sm text-gray-500">{location.address}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-400 mb-4 font-mono bg-gray-50 p-2 rounded">
                    GPS: {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)} | Raio: {location.radius}m
                  </div>
                  
                  <div className="flex gap-2 pt-3 border-t border-gray-100">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="flex-1 text-[#F5A623] hover:text-[#D99015] hover:bg-amber-50"
                      onClick={() => openQrDialog(location)}
                      data-testid={`qr-location-${location.id}`}
                    >
                      <QrCode size={14} className="mr-1" />
                      QR Code
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                      onClick={() => openEditDialog(location)}
                      data-testid={`edit-location-${location.id}`}
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => openDeleteDialog(location)}
                      data-testid={`delete-location-${location.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* QR Code Dialog */}
        <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
          <DialogContent className="bg-white border-gray-200 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gray-800 text-center">QR Code - {selectedLocation?.name}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center py-6">
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-lg mb-4">
                <img 
                  src={LOGO_URL} 
                  alt="Badu" 
                  className="w-16 h-16 mx-auto mb-4 object-contain"
                />
                {selectedLocation?.qr_code && (
                  <img 
                    src={`data:image/png;base64,${selectedLocation.qr_code}`}
                    alt="QR Code"
                    className="w-48 h-48"
                    data-testid="qr-code-image"
                  />
                )}
                <p className="text-center text-gray-800 font-semibold mt-4">{selectedLocation?.name}</p>
                <p className="text-center text-gray-500 text-sm">{selectedLocation?.address}</p>
                <p className="text-center text-xs text-gray-400 mt-2">Raio: {selectedLocation?.radius}m</p>
              </div>
              <Button 
                onClick={downloadQr}
                className="btn-primary text-white font-semibold"
                data-testid="download-qr-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                Baixar QR Code
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-white border-gray-200">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-gray-800">Remover local?</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-500">
                Esta ação não pode ser desfeita. Isso removerá permanentemente {selectedLocation?.name} do sistema.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-gray-200 text-gray-600 hover:bg-gray-50">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="confirm-delete-location"
              >
                Remover
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
